import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Search, CheckCircle, AlertCircle, ChevronDown, ChevronUp, Hash, Eye, Download, ExternalLink } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';
import { useScrollHighlight } from '../hooks/useScrollHighlight';
import {
  adminGetShippingQueue, adminBulkGenerateSerials, adminGetOrderSerials,
  adminMarkSerial, adminGetSerialStats, adminTraceSerial, adminGetAuditLog,
} from '../../services/api';

const BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  manufactured: 'text-zinc-400 bg-zinc-800 border-zinc-700',
  assigned:     'text-blue-400 bg-blue-500/10 border-blue-500/20',
  ready_to_ship:'text-orange-400 bg-orange-500/10 border-orange-500/20',
  shipped:      'text-teal-400 bg-teal-500/10 border-teal-500/20',
};

const ORDER_STATUS = {
  ready_for_production: { label: 'Ready for Production', cls: 'text-green-400 bg-green-500/10 border-green-500/20' },
  in_production:        { label: 'In Production',        cls: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
  serialized:           { label: 'Serialized',           cls: 'text-purple-400 bg-purple-500/10 border-purple-500/20' },
  ready_to_ship:        { label: 'Ready to Ship',        cls: 'text-orange-400 bg-orange-500/10 border-orange-500/20' },
};

function OrderSerialCard({ order, onSerialize }) {
  const [serials, setSerials] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const { token } = useAdminAuth();

  const loadSerials = async () => {
    const data = await adminGetOrderSerials(token, order._id);
    setSerials(data);
  };

  useEffect(() => { if (expanded) loadSerials(); }, [expanded]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await adminBulkGenerateSerials(token, order._id);
      await loadSerials();
      onSerialize();
    } catch (err) { alert(err.message); }
    setGenerating(false);
  };

  const handleMarkSerialized = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${order._id}/mark-serialized`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const e = await res.json(); alert(e.detail || 'Error'); return; }
      onSerialize(); // Refresh parent
    } catch (err) { alert(err.message); }
  };

  const totalQty = order.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0;
  const nfaCount = order.items?.filter(i => i.nfa_item).reduce((s, i) => s + (i.quantity || 0), 0) || 0;
  const serialized = order.serial_count || 0;
  const complete = serialized >= totalQty;
  const statusCfg = ORDER_STATUS[order.status] || { label: order.status, cls: 'text-zinc-400 bg-zinc-800 border-zinc-700' };

  return (
    <div data-testid={`serial-order-${order._id}`} className={`bg-[#1a1a1a] border transition-colors ${complete ? 'border-purple-500/30' : 'border-white/10'}`}>
      <button
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-white/3"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <Link
              to={`/admin/orders/${order._id}`}
              onClick={e => e.stopPropagation()}
              className="font-inter text-sm font-semibold text-orange-400 hover:text-white transition-colors"
              title="View order detail"
            >
              {order.order_number}
            </Link>
            <span className={`font-inter text-[10px] font-bold px-2 py-0.5 border uppercase tracking-wider ${statusCfg.cls}`}>{statusCfg.label}</span>
            {nfaCount > 0 && <span className="flex items-center gap-1 font-inter text-[10px] text-orange-400 border border-orange-500/20 bg-orange-500/10 px-1.5 py-0.5"><Shield size={9} />{nfaCount} NFA</span>}
          </div>
          <div className="flex items-center gap-4">
            <span className="font-inter text-xs text-zinc-500">{order.business_name || order.customer_info?.name || '—'}</span>
            <span className="font-inter text-xs text-zinc-600">·</span>
            <span className="font-inter text-xs text-zinc-500">{order.items?.length} product types · {totalQty} total units</span>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-right">
            <div className={`font-bebas text-xl tracking-wide ${complete ? 'text-purple-400' : 'text-orange-400'}`}>
              {serialized}/{totalQty}
            </div>
            <div className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider">Serialized</div>
          </div>
          {complete ? <CheckCircle size={20} className="text-purple-400" /> : <AlertCircle size={20} className="text-orange-400" />}
          {expanded ? <ChevronUp size={16} className="text-zinc-400" /> : <ChevronDown size={16} className="text-zinc-400" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-white/10 p-5">
          {/* Items */}
          <div className="mb-4">
            <p className="font-inter text-xs text-zinc-500 uppercase tracking-wider mb-2">Order Items</p>
            <div className="space-y-2">
              {order.items?.map((item, i) => (
                <div key={i} className="flex items-center justify-between bg-[#141414] border border-white/5 px-4 py-2.5">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#0f0f0f] overflow-hidden flex-shrink-0">
                      <img src={item.product_image} alt="" className="w-full h-full object-contain" onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                    <div>
                      <span className="font-inter text-sm text-white">{item.product_name}</span>
                      {item.nfa_item && <span className="ml-2 font-inter text-[10px] text-orange-400">[NFA]</span>}
                    </div>
                  </div>
                  <span className="font-bebas text-base text-zinc-400 tracking-wide">×{item.quantity}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Assigned serials */}
          {serials.length > 0 && (
            <div className="mb-4">
              <p className="font-inter text-xs text-zinc-500 uppercase tracking-wider mb-2">Assigned Serial Numbers</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {serials.map(s => (
                  <div key={s.serial_number} data-testid={`serial-${s.serial_number}`} className="flex items-center gap-3 bg-[#0f0f0f] border border-white/5 px-3 py-2">
                    <Hash size={12} className="text-orange-500 flex-shrink-0" />
                    <span className="font-inter text-xs font-mono text-white flex-1">{s.serial_number}</span>
                    {s.nfa_item && <Shield size={10} className="text-orange-400" />}
                    <span className={`font-inter text-[9px] px-1.5 py-0.5 border uppercase tracking-wider ${STATUS_COLORS[s.status] || 'text-zinc-400 bg-zinc-800 border-zinc-700'}`}>{s.status}</span>
                    {!s.marking_date && (
                      <button
                        onClick={async () => { await adminMarkSerial(token, s.serial_number); await loadSerials(); }}
                        className="font-inter text-[9px] text-zinc-500 hover:text-white border border-white/10 hover:border-white/30 px-1.5 py-0.5 transition-colors"
                        title="Mark as physically marked"
                      >Mark</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3 items-center">
            {!complete && (
              <button
                data-testid={`generate-serials-${order._id}`}
                onClick={handleGenerate}
                disabled={generating}
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-inter text-sm font-medium px-4 py-2.5 transition-colors"
              >
                <Hash size={14} />
                {generating ? 'Generating...' : `Generate ${totalQty - serialized} Serial${totalQty - serialized !== 1 ? 's' : ''}`}
              </button>
            )}
            {complete && order.status === 'in_production' && (
              <button
                data-testid={`mark-serialized-${order._id}`}
                onClick={handleMarkSerialized}
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bebas text-sm tracking-widest px-5 py-2.5 transition-colors"
              >
                <CheckCircle size={14} />
                MARK ORDER SERIALIZED
              </button>
            )}
            {(complete && order.status === 'serialized') && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-purple-400 font-inter text-sm">
                  <CheckCircle size={15} />All items serialized
                </div>
                <a
                  href="/admin/shipping"
                  className="flex items-center gap-1.5 font-bebas text-sm tracking-widest text-orange-500 hover:text-white border border-orange-500/40 hover:border-orange-500 px-4 py-2 transition-colors"
                >
                  → CREATE SHIPMENT
                </a>
              </div>
            )}
            {complete && order.status !== 'in_production' && order.status !== 'serialized' && (
              <div className="flex items-center gap-2 text-purple-400 font-inter text-sm">
                <CheckCircle size={15} />All items serialized — ready to ship
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TracePanel() {
  const { token } = useAdminAuth();
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pdfDownloading, setPdfDownloading] = useState('');

  const downloadTracePdf = async (sn, mode) => {
    setPdfDownloading(mode);
    try {
      const res = await fetch(`${BASE}/api/admin/compliance/serial-trace/${encodeURIComponent(sn)}/pdf?mode=${mode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `WM_SerialTrace_${sn}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
    setPdfDownloading('');
  };

  const handleTrace = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const data = await adminTraceSerial(token, query.trim().toUpperCase());
      setResult(data);
    } catch (err) {
      setError(err.message || 'Serial not found');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="serial-trace-panel">
      <form onSubmit={handleTrace} className="flex gap-3 mb-5">
        <input
          data-testid="trace-serial-input"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Enter serial number (e.g. WM-SUPP-2026-A1B2C3)"
          className="flex-1 bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 outline-none transition-colors placeholder-zinc-600 font-mono"
        />
        <button
          data-testid="trace-search-button"
          type="submit"
          disabled={loading}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-inter text-sm font-medium px-5 py-3 transition-colors"
        >
          <Search size={15} />
          {loading ? 'Searching...' : 'Trace'}
        </button>
      </form>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3 mb-4">{error}</div>}

      {result && (
        <div className="flex items-center justify-between mb-4">
          <span className="font-inter text-xs text-zinc-500">Trace result for: <span className="text-orange-400 font-mono">{result.serial?.serial_number}</span></span>
          <div className="flex gap-2">
            <button data-testid="download-trace-pdf-plain"
              onClick={() => downloadTracePdf(result.serial.serial_number, 'plain')}
              disabled={!!pdfDownloading}
              className="flex items-center gap-1.5 font-inter text-xs bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-3 py-1.5 transition-colors">
              <Download size={12} />{pdfDownloading === 'plain' ? '...' : 'PDF (Physical)'}
            </button>
            <button data-testid="download-trace-pdf-branded"
              onClick={() => downloadTracePdf(result.serial.serial_number, 'branded')}
              disabled={!!pdfDownloading}
              className="flex items-center gap-1.5 font-inter text-xs bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 text-white px-3 py-1.5 transition-colors">
              <Download size={12} />{pdfDownloading === 'branded' ? '...' : 'PDF (Branded)'}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="space-y-4" data-testid="trace-result">
          {/* Serial info */}
          <div className="bg-[#1a1a1a] border border-white/10 p-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-bebas text-2xl text-white tracking-wider">{result.serial.serial_number}</span>
                  {result.serial.nfa_item && <span className="flex items-center gap-1 font-inter text-xs font-bold text-orange-400 bg-orange-500/10 border border-orange-500/20 px-2 py-0.5"><Shield size={11} />NFA Item</span>}
                </div>
                <p className="font-inter text-sm text-zinc-400 mt-1">{result.serial.product_name}</p>
              </div>
              <span className={`font-inter text-xs font-bold px-2 py-1 border uppercase tracking-wider ${STATUS_COLORS[result.serial.status] || 'text-zinc-400 bg-zinc-800 border-zinc-700'}`}>
                {result.serial.status}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Category', value: result.serial.category },
                { label: 'Manufacture Date', value: result.serial.manufacture_date ? new Date(result.serial.manufacture_date).toLocaleDateString() : '—' },
                { label: 'Marking Date', value: result.serial.marking_date ? new Date(result.serial.marking_date).toLocaleDateString() : 'Not marked' },
                { label: 'Assigned Order', value: result.serial.assigned_to_order_number || '—' },
              ].map(f => (
                <div key={f.label}>
                  <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider">{f.label}</p>
                  <p className="font-inter text-sm text-white mt-0.5">{f.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Assignment History (immutable) */}
          <div className="bg-[#1a1a1a] border border-white/10">
            <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
              <h3 className="font-bebas text-base text-white tracking-wider">ASSIGNMENT HISTORY</h3>
              <span className="font-inter text-[10px] text-orange-400 border border-orange-500/20 bg-orange-500/10 px-2 py-0.5">IMMUTABLE</span>
            </div>
            <div className="divide-y divide-white/5">
              {result.serial.assignment_history?.map((ev, i) => (
                <div key={i} className="flex items-start gap-4 px-5 py-3">
                  <div className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-inter text-sm text-white">{ev.description}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="font-inter text-[11px] text-zinc-500">{ev.timestamp ? new Date(ev.timestamp).toLocaleString() : '—'}</span>
                      <span className="font-inter text-[11px] text-zinc-600">by {ev.actor}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order & Shipment context */}
          {(result.order || result.shipment) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {result.order && (
                <div className="bg-[#1a1a1a] border border-white/10 p-4">
                  <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Linked Order</p>
                  <p className="font-inter text-sm text-white">{result.order.order_number}</p>
                  <p className="font-inter text-xs text-zinc-500">{result.order.business_name || '—'} · {result.order.status}</p>
                </div>
              )}
              {result.shipment && (
                <div className="bg-[#1a1a1a] border border-white/10 p-4">
                  <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Shipment</p>
                  <p className="font-inter text-sm text-white">{result.shipment.shipment_id}</p>
                  <p className="font-inter text-xs text-zinc-500">{result.shipment.status} · {result.shipment.tracking_number || 'No tracking yet'}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SignOffGenerateSerials({ order, token, BASE, onDone }) {
  const [generating, setGenerating] = useState(false);
  const totalQty = order.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0;

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${BASE}/api/admin/orders/${order._id}/serials/bulk-generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const e = await res.json(); alert(e.detail || 'Failed to generate serials'); return; }
      onDone();
    } catch (e) { alert(e.message); }
    setGenerating(false);
  };

  return (
    <div className="px-5 py-5 bg-orange-500/5 border-t border-orange-500/20">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-bebas text-base text-orange-400 tracking-wider">SERIAL NUMBERS NOT YET GENERATED</p>
          <p className="font-inter text-xs text-zinc-400 mt-0.5">
            {totalQty} unit{totalQty !== 1 ? 's' : ''} need serial numbers before physical sign-off can proceed.
          </p>
        </div>
        <button
          data-testid={`generate-serials-signoff-${order._id}`}
          onClick={handleGenerate}
          disabled={generating}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-sm tracking-widest px-5 py-3 transition-colors whitespace-nowrap ml-4"
        >
          <Hash size={14} />
          {generating ? 'GENERATING...' : `GENERATE ${totalQty} SERIAL${totalQty !== 1 ? 'S' : ''}`}
        </button>
      </div>
    </div>
  );
}

export default function AdminSerialization() {
  const { token } = useAdminAuth();
  const highlightId = useScrollHighlight();
  const [tab, setTab] = useState('signoff');
  const [orders, setOrders] = useState([]);
  const [signOffQueue, setSignOffQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signingOff, setSigningOff] = useState(null); // {serial, initials}
  const [initials, setInitials] = useState('');
  const [signOffNote, setSignOffNote] = useState('');

  const BASE = process.env.REACT_APP_BACKEND_URL;

  const load = () => {
    Promise.all([
      adminGetShippingQueue(token),
      adminGetSerialStats(token),
      fetch(`${BASE}/api/admin/serialization/sign-off-queue`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => []),
    ])
      .then(([q, s, soq]) => {
        // Include in_production orders + serialized orders with 0 serials (need serials generated)
        const inProduction = q.filter(o =>
          ['ready_for_production', 'in_production'].includes(o.status) ||
          (o.status === 'serialized' && (o.serial_count || 0) === 0)
        );
        setOrders(inProduction);
        setStats(s);
        setSignOffQueue(Array.isArray(soq) ? soq : []);
        // Auto-switch tab: if there are items to sign off, show that tab
        if (Array.isArray(soq) && soq.length > 0) setTab('signoff');
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]);

  const handleSignOff = async () => {
    if (!initials.trim()) { alert('Please enter your initials to confirm.'); return; }
    try {
      await fetch(`${BASE}/api/admin/serials/${encodeURIComponent(signingOff.serial_number)}/sign-off`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmed_by: initials.trim(), notes: signOffNote }),
      });
      setSigningOff(null); setInitials(''); setSignOffNote('');
      load();
    } catch (e) { alert(e.message); }
  };

  const loadAudit = () => {
    adminGetAuditLog(token, 'serial_number').then(setAuditLog).catch(console.error);
  };

  useEffect(() => { if (tab === 'audit') loadAudit(); }, [tab]);

  return (
    <div data-testid="admin-serialization-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-bebas text-4xl text-white tracking-wider">SERIALIZATION</h1>
          <p className="font-inter text-sm text-zinc-500 mt-1">Apply and confirm serial numbers, then items go to the shipping queue</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
          {[
            { label: 'Total Serials', val: stats.total, color: 'text-white' },
            { label: 'Manufactured', val: stats.manufactured, color: 'text-zinc-400' },
            { label: 'Assigned', val: stats.assigned, color: 'text-blue-400' },
            { label: 'Shipped', val: stats.shipped, color: 'text-teal-400' },
            { label: 'NFA Items', val: stats.nfa, color: 'text-orange-400' },
          ].map(s => (
            <div key={s.label} className="bg-[#1a1a1a] border border-white/10 p-4 text-center">
              <div className={`font-bebas text-2xl tracking-wider ${s.color}`}>{s.val}</div>
              <div className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-white/10">
        {[
          { key: 'signoff', label: `Sign-Off Queue (${signOffQueue.length})`, urgent: signOffQueue.length > 0 },
          { key: 'orders', label: `Assign Serials (${orders.length})` },
          { key: 'trace', label: 'Serial Trace' },
          { key: 'audit', label: 'Audit Log' },
        ].map(t => (
          <button key={t.key} data-testid={`serial-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 font-inter text-sm px-4 py-2.5 transition-colors border-b-2 -mb-px ${tab === t.key ? 'border-orange-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            {t.label}
            {t.urgent && <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />}
          </button>
        ))}
      </div>

      {/* ===== TAB: SIGN-OFF QUEUE (PRIMARY) ===== */}
      {tab === 'signoff' && (
        <div data-testid="serial-signoff-tab">
          {signOffQueue.length === 0 ? (
            <div className="text-center py-16 bg-[#1a1a1a] border border-white/10">
              <CheckCircle size={40} className="text-zinc-700 mx-auto mb-3" />
              <p className="font-bebas text-xl text-zinc-600 tracking-wider">NO ITEMS AWAITING SIGN-OFF</p>
              <p className="font-inter text-sm text-zinc-600 mt-1">
                Complete runs on the <a href="/admin/shop-floor" className="text-orange-500 hover:underline">Shop Floor</a> to populate this queue.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {signOffQueue.map(order => (
                <div
                  key={order._id}
                  id={`hl-${order._id}`}
                  data-testid={`signoff-order-${order._id}`}
                  className={`bg-[#1a1a1a] border transition-all duration-500 ${
                    highlightId === order._id
                      ? 'border-orange-500 shadow-[0_0_0_3px_rgba(249,115,22,0.3)] shadow-orange-500/30'
                      : 'border-white/10'
                  }`}
                >
                  <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                    <div>
                      <div className="flex items-center gap-3">
                        <Link
                          to={`/admin/orders/${order._id}`}
                          className="font-bebas text-xl text-orange-400 hover:text-white tracking-wider transition-colors"
                          title="View order detail"
                        >
                          {order.order_number}
                        </Link>
                        {order.has_nfa_items && <span className="flex items-center gap-1 font-inter text-[10px] font-bold text-orange-400 border border-orange-500/20 bg-orange-500/10 px-2 py-0.5"><Shield size={9} />NFA</span>}
                        <span className={`font-inter text-[10px] font-bold px-2 py-0.5 border uppercase tracking-wider ${order.all_signed_off ? 'text-green-400 bg-green-500/10 border-green-500/20' : 'text-purple-400 bg-purple-500/10 border-purple-500/20'}`}>
                          {order.all_signed_off ? '✓ All Confirmed' : `${order.signed_off_count}/${order.total_serials} Confirmed`}
                        </span>
                        {/* Back-link to original order detail */}
                        <Link
                          to={`/admin/orders/${order._id}`}
                          className="flex items-center gap-1 font-inter text-[10px] text-zinc-500 hover:text-orange-400 transition-colors"
                          title="View original order detail"
                        >
                          <ExternalLink size={10} />Order
                        </Link>
                      </div>
                      <p className="font-inter text-xs text-zinc-500 mt-0.5">
                        {order.business_name || order.customer_info?.name || '—'} · {order.total_serials} serial{order.total_serials !== 1 ? 's' : ''} to confirm
                      </p>
                    </div>
                    {order.all_signed_off && (
                      <a href="/admin/shipping" className="flex items-center gap-2 text-green-400 font-inter text-sm font-semibold border border-green-500/30 px-3 py-1.5 hover:bg-green-500/10 transition-colors">
                        <CheckCircle size={14} />→ View in Shipping
                      </a>
                    )}
                  </div>
                  {/* If no serials generated yet, show generate action */}
                  {order.total_serials === 0 ? (
                    <SignOffGenerateSerials order={order} token={token} BASE={BASE} onDone={load} />
                  ) : (
                    <div className="divide-y divide-white/5">
                      {(order.serials || []).map(serial => (
                        <div key={serial.serial_number} data-testid={`serial-signoff-${serial.serial_number}`}
                          className={`flex items-center gap-4 px-5 py-4 ${serial.marking_date ? 'bg-green-500/3' : ''}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-0.5">
                              <span className="font-mono text-lg font-bold text-orange-400">{serial.serial_number}</span>
                              {serial.nfa_item && <Shield size={11} className="text-orange-400" />}
                            </div>
                            <p className="font-inter text-sm text-zinc-300">{serial.product_name}</p>
                            {serial.marking_date && (
                              <p className="font-inter text-xs text-green-400 mt-0.5">
                                ✓ Applied and confirmed by <strong>{serial.sign_off_by}</strong> at {new Date(serial.marking_date).toLocaleTimeString()}
                              </p>
                            )}
                            {!serial.marking_date && (
                              <p className="font-inter text-xs text-zinc-500 mt-0.5">
                                Waiting for physical marking confirmation
                              </p>
                            )}
                          </div>
                          {!serial.marking_date ? (
                            <button data-testid={`confirm-serial-${serial.serial_number}`}
                              onClick={() => { setSigningOff(serial); setInitials(''); setSignOffNote(''); }}
                              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bebas text-sm tracking-widest px-4 py-3 transition-colors whitespace-nowrap">
                              ✓ CONFIRM SERIAL APPLIED
                            </button>
                          ) : (
                            <div className="flex items-center gap-1.5 text-green-400">
                              <CheckCircle size={18} />
                              <span className="font-inter text-sm font-semibold">Confirmed</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Action when all signed off — explicit button to push to shipping */}
                  {order.all_signed_off ? (
                    <div className="px-5 py-4 bg-green-500/10 border-t border-green-500/30 flex items-center justify-between">
                      <div>
                        <p className="font-bebas text-lg text-green-400 tracking-wider">
                          ✓ ALL {order.total_serials} SERIAL NUMBERS CONFIRMED
                        </p>
                        <p className="font-inter text-xs text-zinc-400 mt-0.5">
                          This order is ready to be packaged and shipped.
                        </p>
                      </div>
                      <button
                        data-testid={`send-to-shipping-${order._id}`}
                        onClick={async () => {
                          try {
                            await fetch(`${BASE}/api/admin/serialization/orders/${order._id}/send-to-shipping`, {
                              method: 'POST',
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            load();
                          } catch (e) { alert(e.message); }
                        }}
                        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bebas text-base tracking-widest px-5 py-3 transition-colors whitespace-nowrap ml-4"
                      >
                        → SEND TO SHIPPING QUEUE
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Serial Confirmation Modal */}
      {signingOff && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div data-testid="serial-confirm-modal" className="bg-[#111111] border border-orange-500/30 w-full max-w-md p-6">
            <h3 className="font-bebas text-2xl text-white tracking-wider mb-1">CONFIRM SERIAL APPLIED</h3>
            <div className="my-4 bg-[#1e1e1e] border border-white/10 p-4 text-center">
              <div className="font-mono text-2xl font-bold text-orange-400 mb-1">{signingOff.serial_number}</div>
              <div className="font-inter text-sm text-zinc-400">{signingOff.product_name}</div>
              {signingOff.nfa_item && (
                <div className="flex items-center justify-center gap-1.5 mt-2 font-inter text-xs text-orange-400">
                  <Shield size={11} />NFA Item — confirm marking meets ATF requirements
                </div>
              )}
            </div>
            <p className="font-inter text-sm text-zinc-300 mb-4">
              Confirm you have physically applied serial number <strong className="text-orange-400">{signingOff.serial_number}</strong> to this unit and verified it matches exactly.
            </p>
            <div className="mb-3">
              <label className="block font-inter text-xs text-zinc-500 uppercase tracking-wider mb-2">Your Initials / Name *</label>
              <input value={initials} onChange={e => setInitials(e.target.value)}
                className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-base px-4 py-3 outline-none transition-colors text-center font-semibold"
                placeholder="e.g. JW" autoFocus />
            </div>
            <div className="mb-5">
              <label className="block font-inter text-xs text-zinc-500 uppercase tracking-wider mb-2">Notes (optional)</label>
              <input value={signOffNote} onChange={e => setSignOffNote(e.target.value)}
                className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2 outline-none transition-colors"
                placeholder="Any notes..." />
            </div>
            <div className="flex gap-3">
              <button onClick={handleSignOff}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bebas text-xl tracking-widest py-3.5 transition-colors">
                ✓ YES — SERIAL NUMBER APPLIED
              </button>
              <button onClick={() => setSigningOff(null)}
                className="flex-1 border border-white/20 text-zinc-400 hover:text-white font-bebas text-xl tracking-widest py-3.5 transition-colors">
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === 'orders' && (
        <div data-testid="serialization-orders-tab">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-16 bg-[#1a1a1a] border border-white/10">
              <Hash size={40} className="text-zinc-700 mx-auto mb-3" />
              <p className="font-bebas text-xl text-zinc-600 tracking-wider">NO ORDERS NEED SERIALIZATION</p>
              <p className="font-inter text-sm text-zinc-600 mt-1">Orders in 'In Production' or 'Serialized (0 serials)' will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map(o => <OrderSerialCard key={o._id} order={o} onSerialize={load} />)}
            </div>
          )}
        </div>
      )}

      {tab === 'trace' && <TracePanel />}

      {tab === 'audit' && (
        <div data-testid="serial-audit-tab">
          <div className="bg-[#1a1a1a] border border-white/10">
            <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2">
              <h2 className="font-bebas text-lg text-white tracking-wider">SERIAL NUMBER AUDIT LOG</h2>
              <span className="font-inter text-[10px] text-orange-400 border border-orange-500/20 bg-orange-500/10 px-2 py-0.5">APPEND-ONLY • IMMUTABLE</span>
            </div>
            <div className="divide-y divide-white/5 max-h-[500px] overflow-y-auto">
              {auditLog.map((ev, i) => (
                <div key={i} className="flex items-start gap-4 px-5 py-3">
                  <div className="w-2 h-2 rounded-full bg-zinc-600 mt-1.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="font-inter text-xs font-semibold text-white font-mono">{ev.object_id}</span>
                      <span className="font-inter text-[10px] text-orange-400 border border-orange-500/20 bg-orange-500/10 px-1.5 py-0.5 uppercase tracking-wider">{ev.event_type}</span>
                    </div>
                    <p className="font-inter text-xs text-zinc-400 mt-0.5">{ev.notes || JSON.stringify(ev.new_value)}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="font-inter text-[10px] text-zinc-600">{ev.timestamp ? new Date(ev.timestamp).toLocaleString() : '—'}</span>
                      <span className="font-inter text-[10px] text-zinc-700">by {ev.actor}</span>
                    </div>
                  </div>
                </div>
              ))}
              {auditLog.length === 0 && <div className="text-center py-8 text-zinc-600 font-inter text-sm">No audit events yet</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
